import React, { useState, Dispatch, SetStateAction } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  useTheme,
  LinearProgress,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { loginSchema } from "@shared/utils/yupSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import { CustomTextField } from "@shared/components/CustomTextField";
import { clearAll, logout } from "@store/slices/authSlice";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { signInUser } from "@services/supabaseAPI";
import { deleteCurrentUser } from "@services/api";
import { requireStepUp } from "@shared/utils/stepUpManager";
import Box from "@mui/material/Box";
import { useNotification } from "@shared/contexts/NotificationContext";

interface User {
  email: string;
  password: string;
}

interface Props {
  modalVisible: boolean;
  setModalVisible: Dispatch<SetStateAction<boolean>>;
  user: string;
}

const DeleteAccountModal = ({ modalVisible, setModalVisible, user }: Props) => {
  const [checked, setChecked] = useState(false);
  const [deletingData, setDeletingData] = useState(false);

  const userId = useAppSelector((state) => state.user.userId);

  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showError } = useNotification();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<User>({
    resolver: yupResolver(loginSchema),
  });

  //The goodbye route is blocked by AuthContext.tsx
  const submitForm = async (data: User) => {
    if (!checked) {
      return;
    }
    const { error } = await signInUser(data.email, data.password);
    if (error) {
      showError("Invalid login credentials");
      reset();
      return;
    }
    // signInUser establishes a fresh AAL1 session. For MFA-enrolled
    // users we step up here as a client-side proof of intent; the
    // server's requireAAL2 on DELETE /users/me is the load-bearing
    // gate. Cancel aborts the delete.
    if (!(await requireStepUp())) {
      return;
    }
    setDeletingData(true);
    const result = await deleteCurrentUser();
    setDeletingData(false);
    if (!result.success) {
      // Previous flow silently navigated to /goodbye even when the
      // delete failed, leaving the user thinking their account was
      // gone when it wasn't. Surface the error and keep them put so
      // they can retry or contact support.
      showError(result.error.message || "Could not delete account");
      return;
    }
    dispatch(clearAll());
    dispatch(logout());
    navigate("/goodbye");
  };
  const handleCancel = () => {
    setModalVisible(false);
    setChecked(false);
    reset();
  };

  return (
    <Dialog
      open={modalVisible}
      onClose={() => setModalVisible(false)}
      sx={{
        "& .MuiDialog-paper": {
          background: theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle>
        {deletingData
          ? "Please wait while we delete your account and associated data"
          : "Delete Your Account"}
      </DialogTitle>
      {deletingData ? (
        <Box sx={{ width: "300px" }}>
          <LinearProgress />
        </Box>
      ) : (
        <form onSubmit={handleSubmit(submitForm)}>
          <DialogContent>
            <DialogContentText>
              To confirm your account deletion, please enter your email and
              password below:
            </DialogContentText>
            <CustomTextField
              autoFocus
              {...register("email")}
              name="email"
              label="Email"
              error={!!errors.email}
              helperText={errors.email && errors?.email?.message}
              margin="dense"
              fullWidth
            />
            <CustomTextField
              {...register("password")}
              name="password"
              label="Password"
              type="password"
              error={!!errors.password}
              helperText={errors.password && errors?.password?.message}
              margin="dense"
              fullWidth
            />
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Checkbox
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                />
              }
              label={
                <Typography>
                  I acknowledge that this will permanently erase the account and
                  all related data associated with {user}.
                </Typography>
              }
            />
          </DialogContent>
          <DialogActions>
            <Button color="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" color="secondary" disabled={!checked}>
              Delete
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};

export default DeleteAccountModal;
